/**
 * Idle Engine - tempo ativo vs idle; dispara callback ao cruzar para idle (aggregator enfileira USER_IDLE).
 */

import type { IdleState } from './types';

const DEFAULT_IDLE_THRESHOLD_MS = 60 * 1000;
const ACTIVITY_EVENTS = [
  'scroll',
  'click',
  'keydown',
  'mousemove',
  'touchstart',
  'focus',
];

let state: IdleState = {
  activeMs: 0,
  idleMs: 0,
  lastActivityAt: 0,
  isIdle: false,
};

let idleThresholdMs = DEFAULT_IDLE_THRESHOLD_MS;
let pageStartAt = 0;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let activityListenersAttached = false;
let onIdle: (() => void) | null = null;
let onIdleEnd: (() => void) | null = null;

function notify(): void {
  // Callback genérico pode ser usado pelo aggregator para ler estado
}

function checkIdle(): void {
  const now = Date.now();
  const elapsed = now - state.lastActivityAt;
  if (!state.isIdle && elapsed >= idleThresholdMs) {
    state.isIdle = true;
    state.activeMs = state.lastActivityAt - pageStartAt;
    state.idleMs = 0;
    if (onIdle) onIdle();
  }
  if (state.isIdle) {
    state.idleMs = now - (state.lastActivityAt + idleThresholdMs);
  }
  notify();
}

function onActivity(): void {
  const now = Date.now();
  const wasIdle = state.isIdle;
  state.lastActivityAt = now;
  state.isIdle = false;
  if (wasIdle) {
    state.idleMs = 0;
    if (onIdleEnd) onIdleEnd();
  }
  state.activeMs = now - pageStartAt;

  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  idleTimer = setTimeout(checkIdle, idleThresholdMs);
}

export function initIdleBehavior(
  thresholdMs: number = DEFAULT_IDLE_THRESHOLD_MS,
  idleCallback?: () => void,
  idleEndCallback?: () => void
): void {
  if (typeof window === 'undefined') return;
  idleThresholdMs = thresholdMs;
  onIdle = idleCallback ?? null;
  onIdleEnd = idleEndCallback ?? null;
  pageStartAt = Date.now();
  state = {
    activeMs: 0,
    idleMs: 0,
    lastActivityAt: Date.now(),
    isIdle: false,
  };

  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  idleTimer = setTimeout(checkIdle, idleThresholdMs);

  if (!activityListenersAttached) {
    activityListenersAttached = true;
    ACTIVITY_EVENTS.forEach(ev => {
      window.addEventListener(ev, onActivity);
    });
  }
}

export function getIdleState(): IdleState {
  const now = Date.now();
  if (state.isIdle) {
    return {
      ...state,
      idleMs: Math.max(0, now - state.lastActivityAt - idleThresholdMs),
    };
  }
  return {
    ...state,
    activeMs: now - pageStartAt,
  };
}

export function resetIdleState(): void {
  pageStartAt = Date.now();
  state = {
    activeMs: 0,
    idleMs: 0,
    lastActivityAt: Date.now(),
    isIdle: false,
  };
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  idleTimer = setTimeout(checkIdle, idleThresholdMs);
}

export function destroyIdleBehavior(): void {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  if (activityListenersAttached && typeof window !== 'undefined') {
    activityListenersAttached = false;
    ACTIVITY_EVENTS.forEach(ev => {
      window.removeEventListener(ev, onActivity);
    });
  }
  onIdle = null;
  onIdleEnd = null;
}
